import React, { Fragment, useState, useEffect } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import PortalMenu from "../Common/PortalMenu";
import AppPagination from "../Common/AppPagination";
import { Row, Col, Card, Table, Badge, Dropdown, Modal, Button, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import api, { getFileUrl } from "../../../api";
import swal from "sweetalert";
import { useSelector } from "react-redux";
import PermissionWrapper from "../PermissionWrapper";

const VendorList = () => {
 const user = useSelector((state) => state.auth?.userDetails || {});
 const [vendors, setVendors] = useState([]);
 const [categories, setCategories] = useState([]);
 const [selectedVendor, setSelectedVendor] = useState(null);
 const [showModal, setShowModal] = useState(false);
 const [loading, setLoading] = useState(false);
 
 // Pagination & Filter states
 const [pagination, setPagination] = useState({
 currentPage: 1,
 totalPages: 1,
 totalCount: 0,
 hasNextPage: false,
 hasPrevPage: false
 });
 
 const [filters, setFilters] = usePersistentFilters("vendors_filters", {
 search: "",
 category: "",
 status: "",
 page: 1,
 limit: 10
 });

 useEffect(() => {
 fetchVendors();
 }, [filters.page, filters.limit, filters.category, filters.status]);

 useEffect(() => {
 fetchCategories();
 }, []);

 const handlePageChange = (page) => {
 setFilters(prev => ({ ...prev, page: page }));
 };

 const fetchVendors = async () => {
 try {
 setLoading(true);
 const queryParams = new URLSearchParams();
 if (filters.search) queryParams.append('search', filters.search);
 if (filters.category) queryParams.append('category', filters.category);
 if (filters.status) queryParams.append('status', filters.status);
 queryParams.append('page', filters.page);
 queryParams.append('limit', filters.limit);

 const response = await api.get(`/vendors?${queryParams.toString()}`);
 if (response.data.success) {
 setVendors(response.data.data);
 setPagination(response.data.pagination);
 }
 } catch (error) {
 console.error("Error fetching vendors:", error);
 swal("Error", "Failed to fetch vendors", "error");
 } finally {
 setLoading(false);
 }
 };

 const fetchCategories = async () => {
 try {
 const response = await api.get("/vendor-categories");
 if (response.data.success) {
 setCategories(response.data.data);
 }
 } catch (error) {
 console.error("Error fetching categories:", error);
 swal("Error", "Failed to load categories", "error");
 }
 };

 // DELETE VENDOR — SWEETALERT
 const handleDelete = async (id) => {
 const confirm = await swal({
 title: "Are you sure?",
 text: "This vendor will be deleted permanently!",
 icon: "warning",
 buttons: true,
 dangerMode: true,
 });

 if (!confirm) return;

 try {
 const response = await api.delete(`/vendors/${id}`);
 if (response.data.success) {
 swal("Deleted!", "Vendor deleted successfully", "success");
 fetchVendors();
 }
 } catch (error) {
 console.error("Error deleting vendor:", error);
 swal("Error", "Failed to delete vendor", "error");
 }
 };

 const handleView = async (id) => {
 try {
 const response = await api.get(`/vendors/${id}`);
 if (response.data.success) {
 setSelectedVendor(response.data.data);
 setShowModal(true);
 }
 } catch (error) {
 console.error("Error fetching vendor details:", error);
 swal("Error", "Failed to load vendor details", "error");
 }
 };

 const handleFilterChange = (e) => {
 const { name, value } = e.target;
 setFilters(prev => ({ ...prev, [name]: value, page: 1 }));
 };

 const handleSearch = () => {
 fetchVendors();
 };

 const getStatusBadge = (status) => {
 switch (status) {
 case "Active":
 return "success";
 case "Inactive":
 return "warning";
 case "Blacklisted":
 return "danger";
 default:
 return "secondary";
 }
 };

 const svgMenu = (
 <svg width="20px" height="20px" viewBox="0 0 24 24">
 <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
 <circle fill="#000000" cx="5" cy="12" r="2"></circle>
 <circle fill="#000000" cx="12" cy="12" r="2"></circle>
 <circle fill="#000000" cx="19" cy="12" r="2"></circle>
 </g>
 </svg>
 );

 return (
 <Fragment>
 <PageTitle activeMenu="Vendors" motherMenu="Vendor Management" />
 
 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between">
 <Card.Title>Vendor List</Card.Title>
 <PermissionWrapper user={user} required={["create:vendors"]}>
 <Link to="/vendors/add" className="btn btn-primary">
 + Add Vendor
 </Link>
 </PermissionWrapper>
 </Card.Header>

 <Card.Body>
 {/* Filter controls */}
 <div className="mb-4">
 <Row className="align-items-center">
 <Col md={4} className="mb-2 mb-md-0">
 <Form.Group className="mb-0">
 <Form.Control
 type="text"
 name="search"
 placeholder="Search vendors (name, contact, email...)"
 value={filters.search}
 onChange={handleFilterChange}
 onKeyDown={(e) => {
 if (e.key === "Enter") {
 handleSearch();
 }
 }}
 />
 </Form.Group>
 </Col>
 <Col md={3} className="mb-2 mb-md-0">
 <Form.Group className="mb-0">
 <Form.Control
 as="select"
 name="category"
 value={filters.category}
 onChange={handleFilterChange}
 className="form-control default-select"
 >
 <option value="">All Categories</option>
 {categories.map((cat) => (
 <option key={cat._id} value={cat._id}>
 {cat.name}
 </option>
 ))}
 </Form.Control>
 </Form.Group>
 </Col>
 <Col md={3} className="mb-2 mb-md-0">
 <Form.Group className="mb-0">
 <Form.Control
 as="select"
 name="status"
 value={filters.status}
 onChange={handleFilterChange}
 className="form-control default-select"
 >
 <option value="">All Statuses</option>
 <option value="Active">Active</option>
 <option value="Inactive">Inactive</option>
 <option value="Blacklisted">Blacklisted</option>
 </Form.Control>
 </Form.Group>
 </Col>
 <Col md={2}>
 <Button 
 variant="primary" 
 className="w-100" 
 onClick={handleSearch}
 >
 <i className="fas fa-search me-2"></i>Search
 </Button>
 </Col>
 </Row>
 </div>

 {loading ? (
 <div className="text-center py-5">
 <div className="spinner-border text-primary" role="status"></div>
 <p className="mt-3">Loading vendors...</p>
 </div>
 ) : vendors.length === 0 ? (
 <div className="text-center py-5">
 <p className="text-muted">No vendors found matching criteria.</p>
 {(filters.search || filters.category || filters.status) ? (
 <Button 
 variant="light" 
 onClick={() => setFilters({ search: "", category: "", status: "", page: 1, limit: 10 })}
 >
 Clear Filters
 </Button>
 ) : (
 <Link to="/vendors/add" className="btn btn-primary mt-2">
 + Add Vendor
 </Link>
 )}
 </div>
 ) : (
 <div className="table-responsive">
 <Table hover className="align-middle">
 <thead>
 <tr>
 <th style={{ textAlign: "center" }}>#</th>
 <th style={{ textAlign: "center" }}>Vendor Name</th>
 <th style={{ textAlign: "center" }}>Category</th>
 <th style={{ textAlign: "center" }}>Contact Person</th>
 <th style={{ textAlign: "center" }}>Email</th>
 <th style={{ textAlign: "center" }}>Phone</th>
 <th style={{ textAlign: "center" }}>GST</th>
 <th style={{ textAlign: "center" }}>Status</th>
 <th style={{ textAlign: "center" }}>Actions</th>
 </tr>
 </thead>

 <tbody>
 {vendors.map((vendor, i) => (
 <tr key={vendor._id}>
 <td style={{ textAlign: "center" }}>{(pagination.currentPage - 1) * filters.limit + i + 1}</td>
 <td style={{ textAlign: "center" }}><strong>{vendor.name}</strong></td>
 <td style={{ textAlign: "center" }}>{vendor.category?.name || "N/A"}</td>
 <td style={{ textAlign: "center" }}>{vendor.contactPerson}</td>
 <td style={{ textAlign: "center" }}>{vendor.email}</td>
 <td style={{ textAlign: "center" }}>{vendor.phone}</td>
 <td style={{ textAlign: "center" }}>{vendor.gst || "N/A"}</td>

 <td style={{ textAlign: "center" }}>
 <Badge bg={getStatusBadge(vendor.status)}>
 {vendor.status}
 </Badge>
 </td>

 <td>
 <div className="d-flex justify-content-center">
 <Dropdown>
 <Dropdown.Toggle variant="" as="div" className="btn-link i-false">
 {svgMenu}
 </Dropdown.Toggle>

 <Dropdown.Menu align="end" as={PortalMenu}>
 <Dropdown.Item onClick={() => handleView(vendor._id)}>
 <i className="fas fa-eye me-2"></i>View
 </Dropdown.Item>

 <Dropdown.Item as={Link} to={`/vendors/edit/${vendor._id}`}>
 <i className="fas fa-edit me-2"></i>Edit
 </Dropdown.Item>

 <Dropdown.Divider />

 <Dropdown.Item 
 className="text-danger"
 onClick={() => handleDelete(vendor._id)}
 >
 <i className="fas fa-trash me-2"></i>Delete
 </Dropdown.Item>
 </Dropdown.Menu>
 </Dropdown>
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 {/* Pagination */}
 <AppPagination
 currentPage={filters.page}
 totalPages={pagination.totalPages}
 totalCount={pagination.totalCount}
 itemsLength={vendors.length}
 limit={filters.limit}
 onPageChange={handlePageChange}
 />
 </div>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* VIEW VENDOR MODAL */}
 <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
 <Modal.Header closeButton>
 <Modal.Title>Vendor Details</Modal.Title>
 </Modal.Header>

 <Modal.Body>
 {selectedVendor && (
 <>
 <Row>
 <Col md={6}>
 <p><strong>Name:</strong> {selectedVendor.name}</p>
 <p><strong>Category:</strong> {selectedVendor.category?.name || "N/A"}</p>
 <p><strong>Contact Person:</strong> {selectedVendor.contactPerson}</p>
 <p><strong>Email:</strong> {selectedVendor.email}</p>
 </Col>

 <Col md={6}>
 <p><strong>Phone:</strong> {selectedVendor.phone}</p>
 <p><strong>GST:</strong> {selectedVendor.gst || "N/A"}</p>
 <p>
 <strong>Status:</strong>{" "}
 <Badge bg={getStatusBadge(selectedVendor.status)}>
 {selectedVendor.status}
 </Badge>
 </p>
 </Col>
 </Row>

 <hr />
 <Row>
 <Col md={12}>
 <p><strong>Address:</strong> {selectedVendor.address}</p>
 </Col>
 </Row>

 {/* BANK INFO */}
 <hr />
 <h6 className="mb-2">Tax Information</h6>
 <Row>
 <Col md={6}>
 <p><strong>PAN Number:</strong> {selectedVendor.panNumber || "N/A"}</p>
 </Col>
 <Col md={6}>
 <p><strong>Aadhaar Number:</strong> {selectedVendor.aadhaarNumber || "N/A"}</p>
 </Col>
 </Row>
 <Row>
 <Col md={6}>
 <p>
 <strong>PAN Document:</strong>{" "}
 {selectedVendor.panDocument?.fileUrl ? (
 <a href={getFileUrl(selectedVendor.panDocument.fileUrl)} target="_blank" rel="noreferrer">
 {selectedVendor.panDocument.fileName || "Open file"}
 </a>
 ) : (
 "N/A"
 )}
 </p>
 </Col>
 <Col md={6}>
 <p>
 <strong>Aadhaar Document:</strong>{" "}
 {selectedVendor.aadhaarDocument?.fileUrl ? (
 <a href={getFileUrl(selectedVendor.aadhaarDocument.fileUrl)} target="_blank" rel="noreferrer">
 {selectedVendor.aadhaarDocument.fileName || "Open file"}
 </a>
 ) : (
 "N/A"
 )}
 </p>
 </Col>
 </Row>

 {/* BANK INFO */}
 {selectedVendor.bank && (
 <>
 <hr />
 <h6 className="mb-2">Bank Information</h6>
 <Row>
 <Col md={6}>
 <p><strong>Bank Name:</strong> {selectedVendor.bank.bankName || "N/A"}</p>
 </Col>
 <Col md={6}>
 <p><strong>Account Holder:</strong> {selectedVendor.bank.accountHolder || "N/A"}</p>
 </Col>
 </Row>

 <Row>
 <Col md={6}>
 <p><strong>Account No:</strong> {selectedVendor.bank.accountNo || "N/A"}</p>
 </Col>
 <Col md={6}>
 <p><strong>IFSC Code:</strong> {selectedVendor.bank.ifsc || "N/A"}</p>
 </Col>
 </Row>
 </>
 )}

 <hr />
 <Row>
 <Col md={6}>
 <p><strong>Created:</strong> {new Date(selectedVendor.createdAt).toLocaleDateString()}</p>
 </Col>
 <Col md={6}>
 <p><strong>Last Updated:</strong> {new Date(selectedVendor.updatedAt).toLocaleDateString()}</p>
 </Col>
 </Row>
 </>
 )}
</Modal.Body>


 <Modal.Footer>
 <Button variant="secondary" onClick={() => setShowModal(false)}>
 Close
 </Button>

 {selectedVendor && (
 <Button variant="primary" as={Link} to={`/vendors/edit/${selectedVendor._id}`}>
 <i className="fas fa-edit me-2"></i>Edit Vendor
 </Button>
 )}
 </Modal.Footer>
 </Modal>

 </Fragment>
 );
};

export default VendorList;
